import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import type { DateRangeBounds } from '@/components/DateRangePicker';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { AffiliateWithdrawStatus, MonthlyReportData } from '@/services/payment/payment.types';
import * as React from 'react';
import { BulkApproveButton } from './BulkApproveButton';
import { MONTHLY_REPORT_LABELS } from '../constants';
import { getColumns } from './Column';

export function MonthlyReportTable({
  data = [],
  pagination,
  loading,
  monthBounds,
}: {
  data?: MonthlyReportData[];
  pagination: Pagination;
  loading: boolean;
  monthBounds?: DateRangeBounds;
}) {
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getColumns(t), [t]);

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const statusLabels: Record<AffiliateWithdrawStatus, string> = {
    [AffiliateWithdrawStatus.PENDING]: t('common.page.table.pending'),
    [AffiliateWithdrawStatus.APPROVED]: t('common.page.table.approved'),
    [AffiliateWithdrawStatus.REJECTED]: t(MONTHLY_REPORT_LABELS.rejected),
  };

  const exportColumnById: Record<
    string,
    { header: string; value: (row: MonthlyReportData, index: number) => unknown }
  > = {
    sn: { header: t('common.page.tableColumn.sn'), value: (_row, index) => getSerialNumber(table, index) },
    requestId: { header: t('common.page.tableColumn.requestId'), value: (row) => row.requestId },
    createdDate: {
      header: t(MONTHLY_REPORT_LABELS.requestedDate),
      value: (row) => formatDate(row.requestDate, 'YYYY/MM/DD HH:mm:ss'),
    },
    bankType: { header: t(MONTHLY_REPORT_LABELS.bankType), value: (row) => row.bankType },
    bankName: { header: t('common.page.tableColumn.bankInfo'), value: (row) => row.bankName },
    accountSymbol: { header: t('common.page.tableColumn.accountSymbol'), value: (row) => row.accountSymbol || '-' },
    accountNumber: { header: t('common.page.tableColumn.accountNumber'), value: (row) => row.accountNumber },
    accountName: { header: t(MONTHLY_REPORT_LABELS.accountHolderName), value: (row) => row.accountName },
    requestedAmount: {
      header: t(MONTHLY_REPORT_LABELS.requestedAmount),
      value: (row) => formatAmount(row.requestedAmount),
    },
    status: { header: t('common.page.tableColumn.status'), value: (row) => statusLabels[row.status] ?? row.status },
    remarks: { header: t('common.page.tableColumn.remarks'), value: (row) => row.remarks || '-' },
  };

  const handleExport = () => {
    const exportColumns = table
      .getVisibleLeafColumns()
      .flatMap(({ id }) => (exportColumnById[id] ? [exportColumnById[id]] : []));
    const exportRows = table
      .getFilteredRowModel()
      .rows.map(({ original, index }) =>
        Object.fromEntries(exportColumns.map(({ header, value }) => [header, value(original, index)])),
      );

    exportCsvSingleFilePapa(exportRows, {
      fileName: 'affiliate-withdrawal-monthly-report',
      headers: exportColumns.map(({ header }) => header),
    });
  };

  const { table, statusFilter, onStatusFilterChange } = useDataTable({
    data,
    columns,
    pageCount,
    meta: {
      defaultDatePreset: 'today',
      title: t('paymentManagement.affiliatePaymentLogs.monthlyReport.table.title'),
      totalPage: pagination.totalItems,
      loading,
      actionKey: 'exportCsv',
      action: handleExport,
    },
    getRowId: (originalRow: MonthlyReportData) => originalRow.requestId,
    statusFilterKey: 'status',
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable table={table}>
      <DataTableToolbar
        defaultDatePreset="today"
        allowFutureDates
        dateBounds={monthBounds}
        statusFilter={statusFilter as AffiliateWithdrawStatus}
        onStatusFilterChange={onStatusFilterChange}
      />
      <BulkApproveButton />
    </DataTable>
  );
}
